import React from "react";
import { Link } from "react-router-dom";
import { Heart, Star, MessageCircle, Instagram } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { formatINR, imageUrl } from "../lib/api";

export const ProductCard = ({ product }) => {
  const { toggleWishlist, isInWishlist, addToCart } = useShop();
  const wished = isInWishlist(product.id);
  const discount = product.compare_at_price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  return (
    <div className="product-card group relative bg-brand-ivory-soft" data-testid={`product-card-${product.slug}`}>
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative overflow-hidden aspect-[4/5] bg-brand-ivory">
          <img
            src={imageUrl(product.images?.[0])}
            alt={product.name}
            className="product-card-image w-full h-full object-cover"
            loading="lazy"
          />
          {product.is_new && (
            <span className="absolute top-3 left-3 label-tiny bg-brand-ivory text-brand-maroon px-2.5 py-1">New</span>
          )}
          {discount > 0 && (
            <span className="absolute top-3 right-3 label-tiny bg-brand-maroon text-brand-ivory px-2.5 py-1">-{discount}%</span>
          )}
        </div>
      </Link>

      <button
        onClick={() => toggleWishlist(product)}
        className={`absolute top-4 right-4 w-9 h-9 rounded-full backdrop-blur-md bg-brand-ivory/70 flex items-center justify-center border border-brand-gold/30 hover:bg-brand-ivory transition-colors duration-200 ${wished ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        style={{ top: product.is_new || discount > 0 ? '3rem' : '1rem' }}
        aria-label="Add to wishlist"
        data-testid={`wishlist-btn-${product.slug}`}
      >
        <Heart size={16} strokeWidth={1.5} fill={wished ? "#6B111A" : "none"} color="#6B111A" />
      </button>

      <div className="pt-4 pb-6 px-1">
        <div className="flex items-center gap-1 mb-2">
          <Star size={12} fill="#C5A059" color="#C5A059" strokeWidth={0} />
          <span className="text-xs text-brand-muted">{product.rating} · {product.review_count}</span>
        </div>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-serif-brand text-lg text-brand-charcoal group-hover:text-brand-maroon leading-snug transition-colors duration-300">{product.name}</h3>
        </Link>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="text-brand-maroon font-semibold">{formatINR(product.price)}</span>
          {product.compare_at_price && (
            <span className="text-xs text-brand-muted line-through">{formatINR(product.compare_at_price)}</span>
          )}
        </div>
        <button
          onClick={() => addToCart(product, 1, product.variants?.[0] || null)}
          className="w-full mt-4 text-xs tracking-[0.2em] uppercase font-medium py-3 border border-brand-charcoal/70 hover:bg-brand-maroon hover:border-brand-maroon hover:text-brand-ivory transition-colors duration-300"
          data-testid={`add-to-cart-btn-${product.slug}`}
        >
          Add to Cart
        </button>
        <div className="flex gap-2 mt-2" data-testid={`social-shop-${product.slug}`}>
          <a href={`https://wa.me/c/917987780925?text=${encodeURIComponent("Hi! I'd like to know more about " + product.name)}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#25D366]/10 hover:bg-[#25D366] hover:text-white text-[#075E54] text-[10px] tracking-[0.15em] uppercase font-medium transition-colors duration-200" title="Shop on WhatsApp">
            <MessageCircle size={11} strokeWidth={1.8} /> WhatsApp
          </a>
          <a href="https://www.instagram.com/bindni_jewels" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex-1 flex items-center justify-center gap-1.5 py-2 text-white text-[10px] tracking-[0.15em] uppercase font-medium hover:brightness-110 transition-all" style={{ background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)" }} title="Shop on Instagram">
            <Instagram size={11} strokeWidth={1.8} /> Instagram
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
